// InvoiceWithInput.tsx
import React, { useEffect, useRef } from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import './PDFRender.print.css';

// تعریف تایپ‌ها بر اساس داده‌های جدید
interface LoadData {
  crop_year_name: string;
  date: string;
  dirt_weight: number;
  driver_name: string;
  factory_name: string;
  farmer_name: string;
  id: number;
  load_number: string;
  pest_weight: number;
  phone_number: string;
  price_per_kilo: number;
  pure_payable: number;
  pure_weight: number;
  quota_pulp_price: number;
  quota_sugar_price: number;
  rent_help: number;
  sugar_beet_polarity: number;
  total_weight: number;
  transportation_cost: number;
}

interface PoisonData {
  commitment_number: string;
  crop_year_name: string;
  factory_name: string;
  farmer_name: string;
  pesticide_amount: number;
  pesticide_name: string;
  price: number;
  price_for_all_farmers_checkbox: boolean;
  total_price: number | null;
}

interface PaymentData {
  crop_year_name: string;
  farmer_name: string;
  payment_reason_name: string;
  payment_type: number;
  price: number;
}

interface InvoiceWithInputProps {
  dataLoad?: LoadData[];
  dataPayment?: PaymentData[];
  dataSeed?: any[];
  dataPoison?: PoisonData[];
  reportData?: any;
  userinfoData?: any;
  contractorName?: string;
  exportPdfFromProp?: any;
  reportDate?: string;
  dontShowData?: boolean;
  userInfo?: any;
  dontshowSummey?: any;
  farmerInfo?: {
    name: string;
    nationalCode: string;
    phoneNumber: string;
    landAmount: number;
    commitmentAmount: number;
    contractDate: string;
  };
}

const InvoiceWithInput: React.FC<InvoiceWithInputProps> = ({
  dataLoad = [],
  dataPayment = [],
  dataSeed = [],
  reportData,
  dataPoison = [],
  contractorName,
  dontShowData,
  dontshowSummey,
  userinfoData,
  exportPdfFromProp,
  userInfo,
}) => {
  const printRef = useRef<HTMLDivElement>(null);
  const handlePrint = () => window.print();
  const invoiceRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (exportPdfFromProp && invoiceRef.current) {
      exportToPDF();
    }
  }, [exportPdfFromProp]);

  const exportToPDF = async () => {
    if (!invoiceRef.current) return;

    try {
      // ایجاد یک کپی از المنت
      const clonedElement = invoiceRef.current.cloneNode(true) as HTMLElement;

      // تنظیم استایل برای چاپ
      clonedElement.style.width = "210mm";
      clonedElement.style.minWidth = "210mm";
      clonedElement.style.maxWidth = "210mm";
      clonedElement.style.margin = "10px";
      clonedElement.style.padding = "10px";
      clonedElement.style.boxSizing = "border-box";

      // مخفی کردن دکمه دانلود
      const downloadButton = clonedElement.querySelector("button");
      if (downloadButton) {
        downloadButton.style.display = "none";
      }

      // اضافه کردن break برای صفحات
      const pageBreakElements = clonedElement.querySelectorAll(".page-break");

      // اضافه کردن به DOM موقتاً
      clonedElement.style.position = "fixed";
      clonedElement.style.left = "-9999px";
      clonedElement.style.top = "0";
      document.body.appendChild(clonedElement);

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a3", // بهتر است از A4 استفاده کنید
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      // تقسیم محتوا به بخش‌های کوچکتر
      await addContentToPDF(clonedElement, pdf, pageWidth, pageHeight);

      // حذف المنت موقت
      document.body.removeChild(clonedElement);

      pdf.save(`invoice-${userInfo.farmer_name}.pdf`);
    } catch (error) {
      console.error("Error generating PDF:", error);
    }
  };

  // تابع کمکی برای اضافه کردن محتوا به PDF با صفحات متعدد
  const addContentToPDF = async (
    element: any,
    pdf: any,
    pageWidth: any,
    pageHeight: any
  ) => {
    const elementHeight = element.scrollHeight;
    const imgWidth = pageWidth - 20; // حاشیه 10mm از هر طرف
    let currentPosition = 0;
    let pageNumber = 0;

    while (currentPosition < elementHeight) {
      if (pageNumber > 0) {
        pdf.addPage();
      }

      // ایجاد کانواس برای بخش فعلی
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        width: element.scrollWidth,
        height: Math.min(1123, elementHeight - currentPosition), // ارتفاع A4 به پیکسل
        windowWidth: element.scrollWidth,
        windowHeight: Math.min(1123, elementHeight - currentPosition),
        y: currentPosition,
        scrollY: -currentPosition,
      });

      const imgData = canvas.toDataURL("image/png");
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      pdf.addImage(imgData, "PNG", 10, 10, imgWidth, imgHeight);

      currentPosition += 1123; // حرکت به اندازه ارتفاع A4
      pageNumber++;
    }
  };
  // ستون‌های جدول بارها
  const loadColumns = [
    // { header: "سال زراعی", accessorKey: "crop_year_name" },
    { header: "کشاورز", accessorKey: "farmer_name" },
    { header: "کارخانه", accessorKey: "factory_name" },
    { header: "تاریخ", accessorKey: "date" },
    // { header: "همراه", accessorKey: "phone_number" },
    { header: "شماره بارنامه", accessorKey: "load_number" },
    { header: "نام راننده", accessorKey: "driver_name" },
    {
      header: "وزن کل",
      accessorKey: "total_weight",
      cell: (cell: any) => (
        <span>{cell.getValue()?.toLocaleString("fa-IR")}</span>
      ),
    },
    {
      header: "وزن خاک",
      accessorKey: "dirt_weight",
      cell: (cell: any) => (
        <span>{cell.getValue()?.toLocaleString("fa-IR")}</span>
      ),
    },
    {
      header: "وزن آفت",
      accessorKey: "pest_weight",
      cell: (cell: any) => (
        <span>{cell.getValue()?.toLocaleString("fa-IR")}</span>
      ),
    },
    {
      header: "وزن خالص",
      accessorKey: "pure_weight",
      cell: (cell: any) => (
        <span>{cell.getValue()?.toLocaleString("fa-IR")}</span>
      ),
    },
    { header: "قند چغندر", accessorKey: "sugar_beet_polarity" },
    {
      header: "قیمت هر کیلو",
      accessorKey: "price_per_kilo",
      cell: (cell: any) => (
        <span>{cell.getValue()?.toLocaleString("fa-IR")}</span>
      ),
    },
    {
      header: "کرایه حمل",
      accessorKey: "transportation_cost",
      cell: (cell: any) => (
        <span>{cell.getValue()?.toLocaleString("fa-IR")}</span>
      ),
    },
    {
      header: "بهای قند سهمیه",
      accessorKey: "quota_sugar_price",
      cell: (cell: any) => (
        <span>{cell.getValue()?.toLocaleString("fa-IR")}</span>
      ),
    },
    {
      header: "بهای تفاله سهمیه",
      accessorKey: "quota_pulp_price",
      cell: (cell: any) => (
        <span>{cell.getValue()?.toLocaleString("fa-IR")}</span>
      ),
    },
    {
      header: "خالص پرداخت",
      accessorKey: "pure_payable",
      cell: (cell: any) => (
        <span>{cell.getValue()?.toLocaleString("fa-IR")}</span>
      ),
    },
  ];

  // ستون‌های جدول بذرها
  const seedColumns = [
    { header: "سال زراعی", accessorKey: "crop_year_name" },
    { header: "نام کشاورز", accessorKey: "farmer_name" },
    {
      header: " قیمت هر یونیت (تومان)",
      accessorKey: "price",
      cell: (cell: any) => (
        <span>{cell.getValue()?.toLocaleString("fa-IR")}</span>
      ),
    },
    {
      header: "قیمت کل (تومان)",
      accessorKey: "total_price",
      cell: (cell: any) => (
        <span>{cell.getValue()?.toLocaleString("fa-IR")}</span>
      ),
    },
    { header: "نام کارخانه", accessorKey: "factory_name" },
    {
      header: "شماره تعهد",
      accessorKey: "commitment_number",
      cell: (cell: any) => (
        <span>{cell.getValue()?.toLocaleString("fa-IR")}</span>
      ),
    },
    { header: "نام بذر", accessorKey: "seed_name" },
    { header: "تعداد", accessorKey: "seed_amount" },
    { header: "تاریخ ثبت", accessorKey: "created_at" },
  ];

  // ستون‌های جدول سموم
  const poisonColumns = [
    { header: "سال زراعی", accessorKey: "crop_year_name" },
    { header: "نام کشاورز", accessorKey: "farmer_name" },
    {
      header: "قیمت هر لیتر(تومان)",
      accessorKey: "price",
      cell: (cell: any) => (
        <span>{cell.getValue()?.toLocaleString("fa-IR")}</span>
      ),
    },
    {
      header: "قیمت  کل(تومان)",
      accessorKey: "total_price",
      cell: (cell: any) => (
        <span>{cell.getValue()?.toLocaleString("fa-IR")}</span>
      ),
    },

    { header: "نام کارخانه", accessorKey: "factory_name" },
    { header: "شماره تعهد", accessorKey: "commitment_number" },
    { header: "نام سم", accessorKey: "pesticide_name" },
    { header: " تعداد ", accessorKey: "pesticide_amount" },
    { header: " تاریخ ایجاد ", accessorKey: "created_at" },
  ];

  // ستون‌های جدول پرداختی
  const paymentColumns = [
    { header: "سال زراعی", accessorKey: "crop_year_name" },
    {
      header: "مبلغ (تومان)",
      accessorKey: "price",
      cell: (cell: any) => (
        <span>{cell.getValue()?.toLocaleString("fa-IR")}</span>
      ),
    },
    { header: " کشاورز", accessorKey: "farmer_name" },
    { header: "نوع پرداخت", accessorKey: "payment_reason_name" },
    { header: "تاریخ ثبت", accessorKey: "created_at" },
  ];

  // تابع برای رندر کردن جدول
  const renderTable = (columns: any[], data: any[], title: string) => {
    if (!data || data.length === 0) return null;

    return (
      <div className="mb-8 break-inside-avoid">
        {/* هدر جدول */}
        <div className="text-center my-5">
          <p className="text-md text-gray-800">
            {title} <span className="text-blue-600"></span>
          </p>
        </div>

        {/* جدول */}
        <div className="overflow-x-auto w-full rounded-lg border border-gray-200 shadow-sm">
          <table className="w-full border-collapse text-xs table-fixed">
            <thead>
              <tr className="bg-gradient-to-r from-blue-600 to-gray-600 text-white">
                {columns.map((column, index) => (
                  <th
                    key={index}
                    className="
    border border-gray-300
    text-center
    whitespace-normal
    overflow-hidden
    min-w-[70px]
    max-w-[120px]
    h-10
    align-middle
    break-words
    hover:bg-indigo-700
    transition-colors
    duration-200
    px-2
  "
                    style={{
                      lineHeight: "1.2",
                      wordWrap: "break-word",
                      wordBreak: "break-word",
                    }}
                  >
                    <div
                      className="flex items-center justify-center h-full w-full"
                      style={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      <span className="text-[10px] font-medium">
                        {column.header}
                      </span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((row, rowIndex) => (
                <tr
                  key={rowIndex}
                  className={`
        break-inside-avoid
        transition-colors
        duration-150
        hover:bg-blue-50
        ${rowIndex % 2 === 0 ? "bg-gray-50" : "bg-white"}
      `}
                >
                  {columns.map((column, colIndex) => (
                    <td
                      key={colIndex}
                      className="
            p-1.5
            border border-gray-200
            text-center
            whitespace-normal
            overflow-hidden
            min-w-[70px]
            max-w-[120px]
            text-[10px]
            align-middle
            leading-tight
            break-words
            group
            hover:bg-blue-100
            transition-colors
            duration-150
          "
                      title={row[column.accessorKey]?.toString()}
                    >
                      <div className="group-hover:font-medium transition-all duration-150 h-full flex items-center justify-center">
                        {column.cell
                          ? column.cell({
                              row: { original: row, index: rowIndex },
                              getValue: () => row[column.accessorKey],
                            })
                          : (() => {
                              const value = row[column.accessorKey];
                              if (typeof value === "number") {
                                return value.toLocaleString("fa-IR");
                              }
                              return value?.toString() || "-";
                            })()}
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // نام پیمانکارِ خودِ کشاورز
  // ۱) مقداری که صفحه صورتحساب از /farmers_invoice/ پاس می‌دهد
  // ۲) /users-dashboard/  ۳) /users/{user_id}  ۴) /commitment/by-user/{user_id}
  const resolvedContractorName = [
    contractorName,
    reportData?.contractor_name,
    userinfoData?.contractor_name,
    userInfo?.contractor_name,
  ]
    .map((name) => (typeof name === "string" ? name.trim() : ""))
    .find((name) => name !== "");

  const seedData = Array.isArray(dataSeed) ? dataSeed : [];
  // محاسبات خلاصه

  // خلاصه صورتحساب با استفاده از داده‌های API
  const summary: any = {
    totalLoads: reportData?.total_delivered_load || 0,
    totalPureWeight: reportData?.total_weight_delivered || 0,
    totalLoadPayable: reportData?.total_pure_payable || 0,
    totalSeedPrice: reportData?.total_seed_cost || 0,
    totalPoisonPrice: reportData?.total_pesticide_cost || 0,
    totalPaymentPrice: reportData?.total_paid_to_you || 0,
    contractorFee: reportData?.one_percent_deduction || 0,
    sugarQuota: reportData?.sugar_quota || 0,
    wasteQuota: reportData?.waste_quota || 0,
    remainingBalance: reportData?.remaining_to_settle || 0,
    farmingProfit:
      reportData?.your_farming_profit > 0 ? reportData.your_farming_profit : 0,
    grandTotal:
      (reportData?.total_pure_payable || 0) -
      (reportData?.total_seed_cost || 0) -
      (reportData?.total_pesticide_cost || 0) -
      (reportData?.one_percent_deduction || 0) +
      (reportData?.total_paid_to_you || 0),
  };
  // const summary = getSummary();

  return (
    <div>
      <button
        onClick={exportToPDF}
        className="opacity-100"
        style={{
          backgroundColor: "#28a745",
          color: "white",
          padding: "12px 30px",
          margin: "1.5rem",
          border: "none",
          borderRadius: "5px",
          cursor: "pointer",
          fontSize: "14px",
        }}
      >
        دانلود (pdf) صورتحساب
      </button>
      <div
        className={`${dontShowData ? "opacity-0" : "md:opacity-100 opacity-0"}`}
      >
        <div style={{ textAlign: "right" }}></div>
        <div dir="rtl" style={{ padding: "18px" }}>
          {/* پیش‌نمایش صورتحساب */}
          <div
            ref={invoiceRef}
            className="invoice-print"
            style={{
              backgroundColor: "white",
              padding: "20px",
              borderRadius: "10px",
              border: "2px solid #007bff",
              marginTop: "20px",
            }}
          >
            {/* هدر صورتحساب */}
            <div className="text-center py-6 border-b border-gray-200">
              <div className="mb-4">
                <h5 className="text-1xl  text-gray-800 mb-2">
                  سامانه پیمانکاران چغندرقند
                </h5>
              </div>

              <div className="text-center">
                <h5 className="text-1xl  text-gray-800 mb-2">
                  پیمانکار: {resolvedContractorName || "-"}
                </h5>
              </div>
            </div>

            {/* اطلاعات کشاورز */}
            <div
              style={{
                backgroundColor: "#f1f1f1",
                padding: "15px",
                borderRadius: "5px",
                marginBottom: "20px",
              }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                  gap: "10px",
                  fontSize: "14px",
                }}
              >
                {/* اطلاعات جدید userInfo */}
                {userInfo && (
                  <>
                    <div className="flex items-center">
                      <p className="text-md font-bold">نام کشاورز:</p>{" "}
                      {userInfo.farmer_name || "-"}
                    </div>

                    <div className="flex item-center  text-md text-gray-800 ">
                      <p className="text-md font-bold mx-1 ">شماره تعهد:</p>{" "}
                      {userInfo.commitment_number || "-"}
                    </div>
                    <div className="flex item-center  text-md text-gray-800 ">
                      <p className="text-md font-bold mx-1 ">سال زراعی:</p>{" "}
                      {userInfo.crop_year_name || "-"}
                    </div>

                    <div className="flex item-center  text-md text-gray-800 ">
                      <p className="text-md font-bold mx-1 ">استان:</p>{" "}
                      {userInfo.province_name || "-"}
                    </div>
                    <div className="flex item-center  text-md text-gray-800 ">
                      <p className="text-md font-bold mx-1 ">شهر:</p>{" "}
                      {userInfo.city_name || "-"}
                    </div>
                    <div className="flex item-center  text-md text-gray-800 ">
                      <p className="text-md font-bold mx-1 ">روستا:</p>{" "}
                      {userInfo.village_name || "-"}
                    </div>

                    <div className="flex item-center  text-md text-gray-800 ">
                      <p className="text-md font-bold mx-1 ">میزان برداشت:</p>{" "}
                      {userInfo.withdrawal_amount
                        ? `${userInfo.withdrawal_amount} کیلوگرم`
                        : "-"}
                    </div>
                    <div className="flex item-center  text-md text-gray-800 ">
                      <p className="text-md font-bold mx-1 ">تاریخ قرارداد:</p>{" "}
                      {userInfo.date_set || "-"}
                    </div>

                    <div className="flex items-center ">
                      <p className="text-md font-bold mr-1 px-2">
                        شماره همراه:
                      </p>{" "}
                      {userinfoData?.phone_number || "-"}
                    </div>
                    <div className="flex items-center ">
                      <p className="text-md font-bold mr-1 px-2">
                        کد ملی کشاورز:
                      </p>{" "}
                      {userinfoData?.username || "-"}
                    </div>
                    <div className="flex items-center mt-2 text-gray-800">
                      <span className="font-bold ml-2">تاریخ گزارش:</span>
                      {new Date().toLocaleDateString("fa-IR")}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* نمایش تمام جداول به صورت زیر هم */}
            {renderTable(seedColumns, dataSeed, "لیست بذرها")}
            {renderTable(poisonColumns, dataPoison, "لیست سم ها")}
            {renderTable(paymentColumns, dataPayment, "لیست پرداختی")}
            {renderTable(loadColumns, dataLoad, "لیست بارها")}
            {/* خلاصه صورتحساب */}
            {!dontshowSummey && (
              <div>
                <div className="bg-white p-3 border border-gray-200 rounded mt-4">
                  <h4 className="text-center font-bold text-gray-800 mb-3">
                    خلاصه صورتحساب
                  </h4>

                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <div className="p-2 border border-gray-200 rounded text-xs">
                        <div className="text-gray-500">بارهای تحویلی</div>
                        <div className="font-bold">
                          {summary.totalLoads.toLocaleString("fa-IR")} بار
                        </div>
                      </div>
                      <div className="p-2 border border-gray-200 rounded text-xs">
                        <div className="text-gray-500">وزن کل</div>
                        <div className="font-bold">
                          {summary.totalPureWeight.toLocaleString("fa-IR")} تن
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div className="p-2 border border-gray-200 rounded text-xs">
                        <div className="text-gray-500">مبلغ بارها</div>
                        <div className="font-bold">
                          {summary.totalLoadPayable.toLocaleString("fa-IR")}{" "}
                          تومان
                        </div>
                      </div>
                      <div className="p-2 border border-gray-200 rounded text-xs">
                        <div className="text-gray-500">هزینه بذر</div>
                        <div className="font-bold">
                          {summary.totalSeedPrice.toLocaleString("fa-IR")} تومان
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div className="p-2 border border-gray-200 rounded text-xs">
                        <div className="text-gray-500">هزینه سم</div>
                        <div className="font-bold">
                          {summary.totalPoisonPrice.toLocaleString("fa-IR")}{" "}
                          تومان
                        </div>
                      </div>
                      <div className="p-2 border border-gray-200 rounded text-xs">
                        <div className="text-gray-500">پرداختی‌ها</div>
                        <div className="font-bold">
                          {summary.totalPaymentPrice.toLocaleString("fa-IR")}{" "}
                          تومان
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div className="p-2 border border-gray-200 rounded text-xs">
                        <div className="text-gray-500">کارمزد (1%)</div>
                        <div className="font-bold">
                          {summary.contractorFee.toLocaleString("fa-IR")} تومان
                        </div>
                      </div>
                      <div className="p-2 border border-gray-200 rounded text-xs">
                        <div className="text-gray-500">سهمیه شکر</div>
                        <div className="font-bold">
                          {summary.sugarQuota.toLocaleString("fa-IR")} کیلو
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div className="p-2 border border-gray-200 rounded text-xs">
                        <div className="text-gray-500">سهمیه تفاله</div>
                        <div className="font-bold">
                          {summary.wasteQuota.toLocaleString("fa-IR")} کیلو
                        </div>
                      </div>
                      <div className="p-2 border border-gray-200 rounded text-xs">
                        <div className="text-gray-500">سود کشاورزی</div>
                        <div className="font-bold text-green-600">
                          {summary.farmingProfit.toLocaleString("fa-IR")} تومان
                        </div>
                      </div>
                    </div>

                    <div className="p-2 border border-gray-300 rounded bg-gray-50">
                      <div className="text-gray-600 text-xs">
                        مانده تا تسویه
                      </div>
                      <div className="font-bold text-blue-600">
                        {summary.remainingBalance.toLocaleString("fa-IR")} تومان
                      </div>
                    </div>

                    {/* <div className="p-2 border border-gray-300 rounded bg-gray-100">
                      <div className="flex justify-between items-center">
                        <span className="font-bold text-sm">
                          {" "}
                          مانده تا تسویه:
                        </span>
                        <span className="font-bold">
                          {summary.grandTotal.toLocaleString("fa-IR")} تومان
                        </span>
                      </div>
                    </div> */}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceWithInput;
